type Provider = 'BYTESCALE' | 'FIREBASE' | string;

interface GetImageUrlOptions {
  query?: string;
}

const FIREBASE_PREFIX = `https://storage.googleapis.com/${import.meta.env.VITE_UPLOAD_FOLDER}.appspot.com/`;
const BYTESCALE_PREFIX = `https://upcdn.io/G22nhrm/image/${import.meta.env.VITE_UPLOAD_FOLDER}/`;

export function getImageUrl(
  imageUrl: string,
  provider: Provider,
  opts: GetImageUrlOptions = {},
): string {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('data:')) return imageUrl;

  let urlStr = imageUrl;

  if (provider === 'BYTESCALE') {
    if (urlStr.startsWith(FIREBASE_PREFIX)) {
      urlStr = urlStr.replace(FIREBASE_PREFIX, BYTESCALE_PREFIX);
    }

    if (!urlStr.startsWith(BYTESCALE_PREFIX)) {
      return urlStr;
    }

    if (opts.query) {
      const hasQuery = urlStr.includes('?');
      const q = opts.query.startsWith('?') ? opts.query.slice(1) : opts.query;
      urlStr += hasQuery ? `&${q}` : `?${q}`;
    }

    return urlStr;
  }

  if (provider === 'FIREBASE') {
    if (urlStr.startsWith(BYTESCALE_PREFIX)) {
      urlStr = urlStr.replace(BYTESCALE_PREFIX, FIREBASE_PREFIX);
    }
    if (urlStr.endsWith('.crop')) {
      urlStr = urlStr.slice(0, -5);
    }
    return urlStr;
  }
  return urlStr;
}

interface GetImageUrlOptions {
  query?: string; // np. "width=1600&height=900&fit=cover&format=auto"
}

const ENV_BUCKET = import.meta.env.VITE_UPLOAD_FOLDER; // np. "hotelspot-dev"

// Bytescale: baza (bez folderu) + opcjonalny prefiks z folderem
const BYTESCALE_BASE = 'https://upcdn.io/G22nhrm/image/';
const BYTESCALE_RAW_BASE = 'https://upcdn.io/G22nhrm/raw/';
const BYTESCALE_PREFIX_ENV = ENV_BUCKET ? `${BYTESCALE_BASE}${ENV_BUCKET}/` : undefined;

/** Dokleja query bez dublowania '?'. */
function appendQuery(urlStr: string, query?: string): string {
  if (!query) return urlStr;
  const q = query.startsWith('?') ? query.slice(1) : query;
  if (!q) return urlStr;
  return urlStr.includes('?') ? `${urlStr}&${q}` : `${urlStr}?${q}`;
}

/** Rozpoznaje link Firebase w formacie A i B i zwraca { bucket, objectPath } albo null. */
function parseFirebaseUrl(urlStr: string): { bucket: string; objectPath: string } | null {
  try {
    const u = new URL(urlStr);

    // Format A:
    // https://storage.googleapis.com/<bucket>.appspot.com/<PATH>[?query]
    if (u.hostname === 'storage.googleapis.com') {
      // pathname zaczyna się od /<bucket>.appspot.com/...
      // np. /hotelspot-dev.appspot.com/NlMAn6z.../file.png
      const matchA = u.pathname.match(/^\/([^/]+)\.appspot\.com\/(.+)$/);
      if (matchA) {
        const bucket = matchA[1]; // np. "hotelspot-dev"
        const objectPath = matchA[2]; // reszta ścieżki
        return { bucket, objectPath };
      }
    }

    // Format B:
    // https://firebasestorage.googleapis.com/v0/b/<bucket>.appspot.com/o/<PATH>?alt=media&token=...
    if (u.hostname === 'firebasestorage.googleapis.com') {
      // pathname: /v0/b/<bucket>.appspot.com/o/<PATH>
      const matchB = u.pathname.match(/^\/v0\/b\/([^/]+)\.appspot\.com\/o\/(.+)$/);
      if (matchB) {
        const bucket = matchB[1]; // np. "hotelspot-dev"
        const objectPathEncoded = matchB[2]; // encodowana ścieżka
        const objectPath = decodeURIComponent(objectPathEncoded);
        return { bucket, objectPath };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/** Konwersja z Firebase -> Bytescale. Niezależna od ENV_BUCKET. */
function firebaseToBytescale(urlStr: string): string {
  const parsed = parseFirebaseUrl(urlStr);
  if (!parsed) return urlStr;
  const { bucket, objectPath } = parsed;

  // Jeśli mamy ENV_BUCKET i jest taki sam -> użyjemy spójnego prefiksu,
  // w przeciwnym razie zbudujemy URL względem zdetektowanego bucketa.
  const prefix = BYTESCALE_PREFIX_ENV && ENV_BUCKET === bucket
    ? BYTESCALE_PREFIX_ENV
    : `${BYTESCALE_BASE}${bucket}/`;

  return `${prefix}${objectPath}`;
}

/** Konwersja z Bytescale -> Firebase (format A). Stara się trafić w bucket z ENV albo z URL. */
function bytescaleToFirebase(urlStr: string): string {
  try {
    const u = new URL(urlStr);
    if (u.hostname !== 'upcdn.io') return urlStr;

    // Ścieżka: /G22nhrm/image/<bucket>/<objectPath>
    const parts = u.pathname.split('/').filter(Boolean);
    // parts[0] = 'G22nhrm', parts[1] = 'image', parts[2] = <bucket>, parts[3...] = objectPath
    if (parts.length >= 4 && parts[1] === 'image') {
      const bucket = parts[2];
      const objectPath = parts.slice(3).join('/');
      return `https://storage.googleapis.com/${bucket}.appspot.com/${objectPath}`;
    }
    return urlStr;
  } catch {
    return urlStr;
  }
}

export function getBytescaleImageUrl(
  imageUrl: string,
  provider: Provider,
  opts: GetImageUrlOptions = {},
): string {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('data:')) return imageUrl;
  let urlStr = imageUrl;
  if (parseFirebaseUrl(urlStr)) {
    // eslint-disable-next-line no-param-reassign
    provider = 'BYTESCALE';
  }

  if (provider === 'BYTESCALE') {
    const fb = parseFirebaseUrl(urlStr);
    if (fb) {
      urlStr = firebaseToBytescale(urlStr);
    }

    if (urlStr.startsWith(BYTESCALE_RAW_BASE)) {
      urlStr = urlStr.replace(BYTESCALE_RAW_BASE, BYTESCALE_BASE);
    }

    if (!urlStr.startsWith(BYTESCALE_BASE)) {
      return urlStr;
    }

    urlStr = appendQuery(urlStr, opts.query);
    return urlStr;
  }

  if (provider === 'FIREBASE') {
    urlStr = bytescaleToFirebase(urlStr);
    if (urlStr.endsWith('.crop')) {
      urlStr = urlStr.slice(0, -5);
    }
    return urlStr;
  }

  return urlStr;
}
